import { NormalizedProductVariant } from "@cappelendamm/catalogue/crystallize/product-translator.js";
import { isProductDigital } from "./isItemDigital";

/**
 * Product Purchase Display Rules:
 *
 * 1. Open Access Books (has openAccessId):
 *    - Show ONLY Open Access button (prioritize over Allvit)
 *    - Do NOT show Allvit button (even if book is on Allvit)
 *    - Do NOT show purchase button
 *    - Show Open Access description text
 *
 * 2. Allvit E-books (isAllvit=true, isDigital=true, no openAccessId):
 *    - Show ONLY Allvit button
 *    - Do NOT show purchase button
 *    - Do NOT show "Ved kjøp av digitale produkter..." message
 *    - Show Allvit description text
 *    - Show evaluation copy request link (if imprint is Cappelen Damm Akademisk)
 *
 * 3. Regular E-books (isAllvit=false, isDigital=true):
 *    - Show purchase button (if has ddsId)
 *    - Show "Ved kjøp av digitale produkter..." message
 *    - Show evaluation copy preview (if digitalEvaluationCopyUrl exists)
 *
 * 4. Physical Books (isDigital=false):
 *    - Show purchase button
 *    - Show "Fri frakt..." message
 *    - Show evaluation copy request link (if any variant has Allvit AND the imprint is Cappelen Damm Akademisk)
 *    - Show evaluation copy preview (if digitalEvaluationCopyUrl exists)
 *
 * 5. Evaluation Copy Request Link:
 *    - Show for Allvit books if the imprint is Cappelen Damm Akademisk
 *    - Show for regular e-books with evaluationCopyType set
 *    - For physical books, show when any variant has Allvit AND the imprint is Cappelen Damm Akademisk
 *    - Otherwise, do not show
 */

type BookVariant = Extract<NormalizedProductVariant, { itemType: "book" }>;
type DigitalServiceVariant = Extract<
  NormalizedProductVariant,
  { itemType: "digital-service" }
>;

const isBook = (product: NormalizedProductVariant): product is BookVariant =>
  product.itemType === "book";

const isDigitalService = (
  product: NormalizedProductVariant,
): product is DigitalServiceVariant => product.itemType === "digital-service";

const isAllvitBook = (product: NormalizedProductVariant): boolean =>
  isBook(product) && product.itemDetails.educationComponents?.isAllvit === true;

/**
 * Determines if the "Add to Cart" / purchase button should be visible.
 *
 * - Allvit e-books: NO (purchased on Allvit)
 * - Regular e-books / audiobooks: only with a ddsId (downloadable here)
 * - Physical books and non-digital bundles: YES
 * - Digital bundles: NO (they have no ddsId of their own)
 * - Digital services: YES unless restricted or free (free ones are not purchased)
 */
export function shouldShowAddToCartButton(
  product: NormalizedProductVariant,
): boolean {
  // Allvit e-books can only be purchased through the Allvit platform.
  if (isAllvitBook(product) && isProductDigital(product)) return false;

  if (isDigitalService(product))
    return (
      !isProductRestrictedDigitalService(product) &&
      !isFreeDigitalService(product)
    );

  // Physical books and non-digital bundles always show the button.
  if (!isProductDigital(product)) return true;

  // Digital books show it only when downloadable from our platform (has ddsId).
  return isBook(product) && !!product.itemDetails.digitalMeasurements?.ddsId;
}

/**
 * Determines if the Allvit button should be visible.
 *
 * Only books can be on Allvit, and only when flagged isAllvit. Open Access takes
 * priority, so an OA book never shows the Allvit button.
 */
export function shouldShowAllvitButton(
  product: NormalizedProductVariant,
): boolean {
  if (!isBook(product)) return false;

  const { educationComponents } = product.itemDetails;

  // Open Access takes priority: many OA books are also free Allvit trial books,
  // but we prefer sending users to the OA platform (more info and formats).
  if (educationComponents?.openAccessId) return false;

  return educationComponents?.isAllvit === true;
}

/**
 * Determines if the evaluation copy request link should be visible.
 *
 * Shown for books with an evaluationCopyType set. A physical book also shows the
 * link when any sibling variant (from allVariants) is an Allvit book.
 */
export function shouldShowEvaluationCopyRequestLink(
  product: NormalizedProductVariant,
  allVariants: NormalizedProductVariant[] = [],
): boolean {
  if (!isBook(product)) return false;

  // This variant has its own evaluation copy type (e.g. Allvit or regular).
  if (product.itemDetails.evaluationCopyType !== null) return true;

  // The sibling-variant fallback only applies to physical books.
  if (isProductDigital(product)) return false;

  return allVariants.some(
    (variant) => isBook(variant) && variant.itemDetails.isAllvit,
  );
}

/**
 * Determines if the evaluation copy preview should be shown.
 */
export const shouldShowEvaluationCopy = (
  product: NormalizedProductVariant,
): boolean => isBook(product) && !!product.itemDetails.digitalEvaluationCopyUrl;

/**
 * Determines if the digital product warning message should be shown.
 * ("Ved kjøp av digitale produkter gjelder ikke angrerett...")
 *
 * Shown for anything isProductDigital() treats as digital (digital books and
 * digital bundles), except Allvit e-books (purchased on Allvit). Digital
 * services are excluded because isProductDigital() returns false for them.
 */
export function shouldShowDigitalProductMessage(
  product: NormalizedProductVariant,
): boolean {
  return isProductDigital(product) && !isAllvitBook(product);
}

/**
 * Determines if the "Fri frakt..." message should be shown.
 *
 * Shown whenever neither the digital-product warning (shouldShowDigitalProductMessage)
 * nor the digital-service case applies. Note this means it can show for Allvit
 * e-books, since those are digital but don't trigger the digital-product warning.
 */
export function shouldShowFreeFreightMessage(
  product: NormalizedProductVariant,
): boolean {
  return (
    !shouldShowDigitalProductMessage(product) && !isDigitalService(product)
  );
}

export function isProductRestrictedDigitalService(
  product: NormalizedProductVariant,
): boolean {
  return (
    isDigitalService(product) &&
    !!(product.itemDetails.restricted || product.manualOverrideRestrictedAccess)
  );
}

/**
 * Determines if a product is a free digital service (price is zero/unset).
 *
 * Free digital services are never added to the cart: the user only needs a
 * registered account on our store to use the license on the product's own site.
 */
export function isFreeDigitalService(
  product: NormalizedProductVariant,
): boolean {
  if (!isDigitalService(product)) return false;

  return product.price === 0 || product.price == null;
}

// Mirrors EntitlementTopic in netthandel-services: entitlement topics live under
// an "Entitlements" root, and the segment right below the root names the platform.
const ENTITLEMENTS_ROOT_TOPIC = "Entitlements";
const PU_ENTITLEMENT_TOPIC = "PU";

/**
 * Determines if a product grants a PU entitlement.
 *
 * PU platforms are only reachable with a Feide login, so private customers can
 * never redeem such a product even though it is technically purchasable.
 */
export function isPuProduct(product: NormalizedProductVariant): boolean {
  return (product.topics ?? []).some((topic) => {
    const [root, entitlement] = (topic?.pathLabel ?? "").split("/");
    return (
      root === ENTITLEMENTS_ROOT_TOPIC && entitlement === PU_ENTITLEMENT_TOPIC
    );
  });
}
